import React, { useState, useEffect } from 'react';
import { Mic, Sparkles, RotateCcw, Check, WifiOff } from 'lucide-react';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { useTokenCheck } from '../../hooks/useTokenCheck';

interface AIDescriptionFieldProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  rows?: number;
  context?: any; // Additional context for AI formatting (project, task, etc.)
  className?: string;
  disabled?: boolean;
}

export const AIDescriptionField: React.FC<AIDescriptionFieldProps> = ({
  value,
  onChange,
  label,
  placeholder,
  rows = 4,
  context = {},
  className = '',
  disabled = false
}) => {
  const { t } = useTranslation();
  const { handleTokenError } = useTokenCheck();
  const [isRecording, setIsRecording] = useState<boolean>(false);
  const [isFormattingAI, setIsFormattingAI] = useState<boolean>(false);
  const [originalNotes, setOriginalNotes] = useState<string>('');
  const [aiFormattedNotes, setAiFormattedNotes] = useState<string>('');
  const [showAIPreview, setShowAIPreview] = useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(navigator.onLine);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const handleStartRecording = async () => {
    if (!isOnline) {
      return;
    }

    if (isRecording) {
      setIsRecording(false);
      return;
    }

    setIsRecording(true);
    
    try {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (!SpeechRecognition) {
        console.error('Speech recognition not supported');
        setIsRecording(false);
        return;
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            finalTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        if (finalTranscript) {
          onChange(value + finalTranscript);
        }
      };

      recognition.onerror = (event: any) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
      };

      recognition.onend = () => {
        setIsRecording(false);
      };

      recognition.start();
    } catch (error) {
      console.error('Failed to start speech recognition:', error);
      setIsRecording(false);
    }
  };

  const handleFormatWithAI = async () => {
    if (!isOnline) {
      return;
    }

    setIsFormattingAI(true);
    setOriginalNotes(value);
    
    try {
      const token = localStorage.getItem('token');
      
      const aiContext = {
        notes: value.trim(),
        ...context
      };
      
      const response = await fetch(`${apiUrl}/api/ai/format-notes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(aiContext)
      });

      const data = await response.json();

      // Check for token errors (402 status)
      if (handleTokenError(response, data)) {
        // Token purchase modal will be shown automatically
        setErrorMessage(data.message || 'Insufficient tokens. Please purchase more tokens to continue using AI features.');
        setIsFormattingAI(false);
        return;
      }

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to format notes');
      }

      setErrorMessage('');
      setAiFormattedNotes(data.formattedNotes || data.data?.formattedNotes);
      setShowAIPreview(true);
    } catch (error) {
      console.error('Failed to format with AI:', error);
      setErrorMessage(error instanceof Error ? error.message : 'Failed to format notes with AI. Please try again.');
    } finally {
      setIsFormattingAI(false);
    }
  };

  const handleAcceptAI = () => {
    onChange(aiFormattedNotes);
    setShowAIPreview(false);
    setAiFormattedNotes('');
    setOriginalNotes('');
  };

  const handleRevertAI = () => {
    onChange(originalNotes);
    setShowAIPreview(false);
    setAiFormattedNotes('');
    setOriginalNotes('');
  };

  const handleClear = () => {
    onChange('');
    setOriginalNotes('');
    setAiFormattedNotes('');
    setShowAIPreview(false);
  };

  return (
    <div>
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}
      <div className="relative">
        <textarea
          value={showAIPreview ? aiFormattedNotes : value}
          onChange={(e) => onChange(e.target.value)}
          rows={rows}
          placeholder={placeholder || t('timesheet.addNotesPlaceholder') || 'Add notes...'}
          disabled={showAIPreview || disabled}
          className={`w-full px-3 py-2 pr-36 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 resize-y min-h-[100px] ${
            showAIPreview ? 'bg-accent-50 dark:bg-gray-700 dark:border dark:border-accent-800/50' : ''
          } ${className}`}
        />
        <div className="absolute bottom-3 right-3 flex gap-2">
          {value.trim() && !showAIPreview && (
            <button
              type="button"
              onClick={handleClear}
              className="p-2 rounded-full bg-gray-400 hover:bg-gray-500 text-white transition-all"
              title={t('timesheet.startAgain') || 'Clear'}
            >
              <RotateCcw className="w-4 h-4" />
            </button>
          )}
          <button
            type="button"
            onClick={handleStartRecording}
            disabled={showAIPreview || !isOnline || disabled}
            className={`p-2 rounded-full transition-all relative ${
              isRecording 
                ? 'bg-error-500 text-white animate-pulse' 
                : !isOnline
                  ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                  : 'bg-accent-500 hover:bg-accent-600 text-white disabled:opacity-50'
            }`}
            title={!isOnline 
              ? t('timesheet.voiceTranscriptionOffline') || 'Voice transcription unavailable offline'
              : t('timesheet.voiceTranscription') || 'Voice transcription'
            }
          >
            <Mic className={`w-4 h-4 ${!isOnline ? 'line-through' : ''}`} />
          </button>
          <button
            type="button"
            onClick={handleFormatWithAI}
            disabled={showAIPreview || isFormattingAI || !isOnline || disabled}
            className={`p-2 rounded-full transition-all ${
              !isOnline
                ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                : 'bg-purple-500 hover:bg-purple-600 text-white disabled:opacity-50'
            }`}
            title={!isOnline
              ? t('timesheet.aiFormattingOffline') || 'AI formatting unavailable offline'
              : t('timesheet.generateNotesWithAI') || 'Generate notes with AI - Click to get an AI-generated job description'
            }
          >
            {isFormattingAI ? (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            ) : (
              <Sparkles className={`w-4 h-4 ${!isOnline ? 'line-through' : ''}`} />
            )}
          </button>
        </div>
      </div>

      {/* AI Preview Actions */}
      {showAIPreview && (
        <div className="flex gap-2 mt-2">
          <button
            type="button"
            onClick={handleAcceptAI}
            className="flex-1 px-3 py-2 bg-success-500 hover:bg-success-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
          >
            <Check className="w-4 h-4" />
            {t('timesheet.accept') || 'Accept'}
          </button>
          <button
            type="button"
            onClick={handleRevertAI}
            className="flex-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors flex items-center justify-center gap-1"
          >
            <RotateCcw className="w-4 h-4" />
            {t('timesheet.revert') || 'Revert'}
          </button>
        </div>
      )}
      
      {showAIPreview && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          {t('timesheet.aiFormattedVersion') || 'AI formatted version'}
        </p>
      )}
      
      {errorMessage && (
        <p className="text-xs text-error-600 dark:text-error-400 mt-1">
          {errorMessage}
        </p>
      )}
    </div>
  );
};

